import React, { useState, useEffect } from 'react';
import { FolderKanban } from 'lucide-react';
import { getProjectByAuctionId } from '../../services/projectService';
import { useProjectByAuction } from '../../context/ProjectsByAuctionContext';

/**
 * Shows a "part of project X" badge for a procedure.
 *
 * Data source priority:
 *  1. If wrapped in <ProjectsByAuctionProvider>, reads from the batch (no API call).
 *     Used by list pages to avoid N+1.
 *  2. Otherwise falls back to a single API call.
 *     Used on detail pages where there's just one auction per page.
 */
const ProjectBadge = ({ auctionId, variant = 'section' }) => {
  // Batch data from context (if available)
  const batch = useProjectByAuction(auctionId);

  const [projectInfo, setProjectInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // CASE 1: wrapped in a provider — the provider owns the fetching.
    // NEVER fall back to individual fetch; just wait for / read the batch result.
    if (batch.hasProvider) {
      if (batch.loading) {
        setLoading(true);
        return;
      }
      if (batch.hit) {
        const info = batch.info;
        setProjectInfo(info ? { found: true, ...info } : { found: false });
      } else {
        // Provider finished, but our id wasn't in the batch → treat as "no project"
        setProjectInfo({ found: false });
      }
      setLoading(false);
      return;
    }

    // CASE 2: no provider (e.g. detail pages) — fetch individually.
    if (!auctionId) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    const fetchProject = async () => {
      try {
        const data = await getProjectByAuctionId(auctionId);
        if (!cancelled) setProjectInfo(data);
      } catch {
        if (!cancelled) setProjectInfo({ found: false });
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchProject();
    return () => { cancelled = true; };
  }, [auctionId, batch.hasProvider, batch.hit, batch.info, batch.loading]);

  // ── Varianta compacta (rezultate cautare / AuctionItem) ──
  if (variant === 'compact') {
    if (loading) {
      return (
        <div className="flex items-center gap-2 mt-2 animate-pulse">
          <div className="w-4 h-4 bg-gray-200 rounded" />
          <div className="h-3.5 bg-gray-200 rounded w-32" />
        </div>
      );
    }

    if (!projectInfo?.found) {
      return (
        <div className="flex items-center gap-1.5 mt-2 text-gray-400">
          <FolderKanban size={14} className="flex-shrink-0" />
          <span className="text-xs">Nu face parte dintr-un proiect</span>
        </div>
      );
    }

    return (
      <a
        href={`/proiecte/${projectInfo.projectId}`}
        className="flex items-center gap-1.5 mt-2 group"
        onClick={(e) => e.stopPropagation()}
      >
        <FolderKanban size={14} className="text-emerald-500 flex-shrink-0" />
        <span className="text-xs font-semibold text-emerald-600 group-hover:text-emerald-700 truncate">
          {projectInfo.projectTitle}
        </span>
        <span className="text-[10px] text-gray-400 flex-shrink-0">
          ({projectInfo.procedureCount} proc.)
        </span>
      </a>
    );
  }

  // ── Varianta sectiune (detail pages) ──
  if (loading) {
    return (
      <div className="bg-gradient-to-br from-emerald-50 via-white to-teal-50 border border-emerald-200 rounded-xl shadow-lg p-4 h-full animate-pulse">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-9 h-9 bg-emerald-100 rounded-lg" />
          <div className="h-5 bg-gray-200 rounded w-24" />
        </div>
        <div className="h-4 bg-gray-200 rounded w-3/4 mb-2" />
        <div className="h-4 bg-gray-200 rounded w-1/2" />
      </div>
    );
  }

  if (!projectInfo?.found) {
    return (
      <div className="bg-gradient-to-br from-gray-50 via-white to-slate-50 border border-gray-200 rounded-xl shadow-lg p-4 flex flex-col justify-between gap-3 h-full">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="w-9 h-9 bg-gray-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <FolderKanban size={20} className="text-gray-400" />
            </div>
            <div>
              <h2 className="text-base font-bold text-gray-500 leading-tight">Proiect</h2>
              <span className="text-[10px] text-gray-400 font-medium">Grupare proceduri</span>
            </div>
          </div>
          <p className="text-gray-500 text-xs leading-snug mb-2">
            Aceasta procedura nu a fost identificata ca facand parte dintr-un proiect grupat.
          </p>
          <ul className="space-y-1 mb-3">
            {['Procedurile similare sunt grupate automat', 'Gruparea se bazeaza pe titlu si autoritate', 'Proiectele noi apar dupa sincronizare'].map((item, idx) => (
              <li key={idx} className="flex items-center gap-1.5 text-xs text-gray-400">
                <svg className="w-3.5 h-3.5 text-gray-300 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20 12H4" />
                </svg>
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <div className="w-full px-4 py-2.5 rounded-xl text-sm font-bold text-center bg-gray-100 text-gray-400 cursor-default">
            Niciun proiect asociat
          </div>
        </div>
      </div>
    );
  }

  return (
    <a
      href={`/proiecte/${projectInfo.projectId}`}
      className="block bg-gradient-to-br from-emerald-50 via-white to-teal-50 border border-emerald-200 rounded-xl shadow-lg p-4 flex flex-col justify-between gap-3 h-full hover:shadow-xl hover:border-emerald-300 transition-all duration-200 group"
      onClick={(e) => e.stopPropagation()}
    >
      <div>
        <div className="flex items-center gap-2 mb-2">
          <div className="w-9 h-9 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0 group-hover:bg-emerald-200 transition-colors">
            <FolderKanban size={20} className="text-emerald-600" />
          </div>
          <div>
            <h2 className="text-base font-bold text-emerald-700 leading-tight">Parte din proiect</h2>
            <span className="text-[10px] text-emerald-400 font-medium">{projectInfo.procedureCount} proceduri grupate</span>
          </div>
        </div>
        <p className="text-gray-700 text-xs leading-snug mb-2 font-semibold">
          {projectInfo.projectTitle}
        </p>
        <ul className="space-y-1 mb-3">
          {[
            projectInfo.authorityName,
            `${projectInfo.procedureCount} proceduri in acest proiect`,
            'Click pentru detalii proiect',
          ].map((item, idx) => (
            <li key={idx} className="flex items-center gap-1.5 text-xs text-gray-600">
              <svg className="w-3.5 h-3.5 text-emerald-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" d="M5 13l4 4L19 7" />
              </svg>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <div className="w-full px-4 py-2.5 rounded-xl text-sm font-bold text-center bg-gradient-to-r from-emerald-600 to-teal-600 text-white group-hover:from-emerald-700 group-hover:to-teal-700 shadow-md transition">
          Vezi proiectul
        </div>
      </div>
    </a>
  );
};

export default ProjectBadge;
